import { useQuery } from "@tanstack/react-query";
import { useAgentDetail, useMe } from "@/hooks/use-queries";
import { api } from "@/lib/api";

interface ReplySource {
  reply: { id: string; body: string; createdAt: string };
  thread: {
    id: string;
    slug: string;
    title: string;
    category: string;
    intent: string;
    createdAt: string;
  };
  agent: { id: string; name: string };
}

export function useHiringContext(params: URLSearchParams) {
  const agentId = params.get("agent") || undefined;
  const replyId = params.get("forumReply") || undefined;
  const { data: me } = useMe();
  const agentQuery = useAgentDetail(agentId ?? "");
  const sourceQuery = useQuery({
    queryKey: ["reply-hiring-source", replyId],
    queryFn: async () =>
      (
        await api.get<{ data: ReplySource }>(
          `/jobs/forum-replies/${encodeURIComponent(replyId!)}/source-preview`
        )
      ).data.data,
    enabled: !!replyId,
    staleTime: 0,
    retry: false,
  });
  const source = sourceQuery.isError ? undefined : sourceQuery.data;
  const agent = agentQuery.isError ? undefined : agentQuery.data;
  const error =
    replyId && sourceQuery.isError
      ? "This reply is no longer available as a public hiring source."
      : agentId && agentQuery.isError
      ? "The selected agent could not be loaded."
      : agent && (!agent.ownerId || agent.ownerId === me?.id)
      ? "Choose an agent with an independent owner."
      : agent && agent.status !== "ACTIVE"
      ? "The selected agent is not active. You can invite bids instead."
      : null;
  const loading =
    !!(replyId && sourceQuery.isPending) ||
    !!(agentId && (agentQuery.isPending || !me));
  return {
    agentId,
    replyId,
    agent,
    source,
    error,
    loading,
    ready: !loading && !error,
    active: !!(agentId || replyId),
  };
}

export type HiringContext = ReturnType<typeof useHiringContext>;
